import { toDisplayDate } from "@/lib/utils/dates";
import { type BenchmarkFilters } from "@/routes/(authed)/_layout.benchmarks";

type DateFilterType = "Opened" | "Closed";

export const toggleDateFilter = (
  filters: BenchmarkFilters,
  inactiveFilters: BenchmarkFilters,
  dateType: DateFilterType,
) => {
  const afterKey = `date${dateType}After` as const;
  const beforeKey = `date${dateType}Before` as const;

  // If dates are in active filters, move them to inactive
  if (filters[afterKey] || filters[beforeKey]) {
    return {
      filters: {
        ...filters,
        [afterKey]: undefined,
        [beforeKey]: undefined,
      },
      inactiveFilters: {
        ...inactiveFilters,
        [afterKey]: filters[afterKey],
        [beforeKey]: filters[beforeKey],
      },
    };
  }
  // If dates are in inactive filters, move them back to active
  else if (inactiveFilters[afterKey] || inactiveFilters[beforeKey]) {
    return {
      filters: {
        ...filters,
        [afterKey]: inactiveFilters[afterKey],
        [beforeKey]: inactiveFilters[beforeKey],
      },
      inactiveFilters: {
        ...inactiveFilters,
        [afterKey]: undefined,
        [beforeKey]: undefined,
      },
    };
  }
  return { filters, inactiveFilters };
};

export const removeDateFilter = (
  filters: BenchmarkFilters,
  inactiveFilters: BenchmarkFilters,
  dateType: DateFilterType,
) => {
  const afterKey = `date${dateType}After` as const;
  const beforeKey = `date${dateType}Before` as const;

  return {
    filters: {
      ...filters,
      [afterKey]: undefined,
      [beforeKey]: undefined,
    },
    inactiveFilters: {
      ...inactiveFilters,
      [afterKey]: undefined,
      [beforeKey]: undefined,
    },
  };
};

export const formatDateRange = (
  filters: BenchmarkFilters,
  inactiveFilters: BenchmarkFilters,
  dateType: DateFilterType,
) => {
  const afterKey = `date${dateType}After` as const;
  const beforeKey = `date${dateType}Before` as const;

  const after = filters[afterKey] || inactiveFilters[afterKey];
  const before = filters[beforeKey] || inactiveFilters[beforeKey];

  return `${dateType}: ${toDisplayDate(after)}${before ? `-${toDisplayDate(before)}` : ""}`;
};

export const hasDateFilter = (
  filters: BenchmarkFilters,
  inactiveFilters: BenchmarkFilters,
  dateType: DateFilterType,
) => {
  const afterKey = `date${dateType}After` as const;
  const beforeKey = `date${dateType}Before` as const;

  return Boolean(filters[afterKey] || filters[beforeKey] || inactiveFilters[afterKey] || inactiveFilters[beforeKey]);
};

export const hasActiveDateFilter = (filters: BenchmarkFilters, dateType: DateFilterType) => {
  const afterKey = `date${dateType}After` as const;
  const beforeKey = `date${dateType}Before` as const;

  return Boolean(filters[afterKey] || filters[beforeKey]);
};

export const formatWeightRange = (min: string | undefined, max: string | undefined) => {
  if (min && max) return `Avg. Weight In: ${min}-${max} lbs`;
  if (min) return `Avg. Weight In: >${min} lbs`;
  if (max) return `Avg. Weight In: <${max} lbs`;
  return "";
};

export const toggleWeightFilter = (filters: BenchmarkFilters, inactiveFilters: BenchmarkFilters) => {
  if (filters.weightAverageInMin || filters.weightAverageInMax) {
    return {
      filters: {
        ...filters,
        weightAverageInMin: undefined,
        weightAverageInMax: undefined,
      },
      inactiveFilters: {
        ...inactiveFilters,
        weightAverageInMin: filters.weightAverageInMin,
        weightAverageInMax: filters.weightAverageInMax,
      },
    };
  } else if (inactiveFilters.weightAverageInMin || inactiveFilters.weightAverageInMax) {
    return {
      filters: {
        ...filters,
        weightAverageInMin: inactiveFilters.weightAverageInMin,
        weightAverageInMax: inactiveFilters.weightAverageInMax,
      },
      inactiveFilters: {
        ...inactiveFilters,
        weightAverageInMin: undefined,
        weightAverageInMax: undefined,
      },
    };
  }
  return { filters, inactiveFilters };
};

export const removeWeightFilter = (filters: BenchmarkFilters, inactiveFilters: BenchmarkFilters) => {
  return {
    filters: {
      ...filters,
      weightAverageInMin: undefined,
      weightAverageInMax: undefined,
    },
    inactiveFilters: {
      ...inactiveFilters,
      weightAverageInMin: undefined,
      weightAverageInMax: undefined,
    },
  };
};

export const hasWeightFilter = (filters: BenchmarkFilters, inactiveFilters: BenchmarkFilters) => {
  return Boolean(
    filters.weightAverageInMin ||
      filters.weightAverageInMax ||
      inactiveFilters.weightAverageInMin ||
      inactiveFilters.weightAverageInMax,
  );
};

export const hasActiveWeightFilter = (filters: BenchmarkFilters) => {
  return Boolean(filters.weightAverageInMin || filters.weightAverageInMax);
};
